import React, { useState, useEffect, useRef, useContext } from 'react'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'
import { formatCurrency } from '../shared/utils'
import DeleteModal from '../shared/DeleteModal'
import EditOrderItem from './Modals/EditOrderItem'
import OrderSummary from './Modals/OrderSummary'
import SuccessModal from './Modals/SuccessModal'
import AlternativesModal from '../Recommendations/AlternativesModal'
import server from '../../server'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

const MyOrders = ({
  myOrder,
  handleDeleteItem,
  clear,
  editItem,
  handleMyOrder,
  catalogueProducts = [],
  orderSummaryVisible,
  setOrderSummaryVisible,
}) => {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const isGroupMember = !!(user?.pharmacy?.pharmacyGroup || user?.pharmacyGroup || user?.pharmacyGroupId)
  const [deleteOrderItem, setDeleteOrderItem] = useState(false)
  const [isModalVisible, setModalVisible] = useState(false)
  const [myOrderData, setMyOrderData] = useState([])
  const [rowItemData, setRowItemData] = useState({})
  const [tableLoading, setTableLoading] = useState(false)
  const [succcess, setSuccess] = useState(false)
  const [paPercentage, setPaPercentage] = useState(0)
  const [alternativesVisible, setAlternativesVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [replaceMode, setReplaceMode] = useState(false)
  const [productToReplace, setProductToReplace] = useState(null)
  const [isAddFromAltModalVisible, setAddFromAltModalVisible] = useState(false)
  const [productToAdd, setProductToAdd] = useState({})
  const [state, setState] = useState(() => getInitialPaginationState(user))
  const [products, setProducts] = useState([])
  const [paResponseData, setPaResponseData] = useState(null)
  // Items not ordered (manufacturer below minimum) that stay in the draft
  const [keptInDraft, setKeptInDraft] = useState([])
  const orderTableRef = useRef(null)

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  useEffect(() => {
    if (myOrder) {
      setMyOrderData(myOrder)
    }
  }, [myOrder])

  // Order placed: reset the page and keep only the items that weren't ordered.
  // (No reset on mount or when the success modal closes; the former wiped the
  // saved draft on page load.)
  useEffect(() => {
    if (succcess === true) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setMyOrderData(keptInDraft)
      clear(products, keptInDraft)
    }
  }, [succcess])

  // const handleClear = (products) => {
  //   clear(products)
  // }
  const calculateTotal = () => {
    let total = 0

    myOrderData.forEach((order) => {
      if (Array.isArray(order.subtotal)) {
        total += order.subtotal.reduce((acc, curr) => acc + curr, 0)
      } else {
        total += order.subtotal || 0
      }
    })

    return total
  }

  const placedSelectedOrder = myOrderData.map((item) => ({
    upc: item.retailUpcCode,
    // description: item.itemDescription,
    price: item.sellingPrice,
    quantity: item.quantity,
    subtotal: item.subtotal,
  }))
  const handleProducts = (products) => {
    setProducts(products)
  }

  const handleCheckAlternatives = (product) => {
    setProductToReplace(product)
    setSelectedProduct(product)
    setReplaceMode(true)
    setAlternativesVisible(true)
  }

  const handleAddToCartFromAlternatives = (product) => {
    setProductToAdd({
      ...product,
      quantity: 1,
      subtotal: product.sellingPrice || 0,
    })
    setAddFromAltModalVisible(true)
  }

  const handleProductReplacement = (replacementProduct) => {
    if (!productToReplace || !replacementProduct) return

    // Remove the old product first
    handleDeleteItem(productToReplace)

    // Add the replacement product with the same quantity
    const replacementOrderItem = {
      ...replacementProduct,
      id: replacementProduct.id || `replacement-${Date.now()}`,
      quantity: productToReplace.quantity,
      subtotal: productToReplace.quantity * replacementProduct.sellingPrice,
      tempQuantity: productToReplace.quantity
    }

    // Use handleMyOrder to add the replacement product (it handles validation and state updates)
    if (handleMyOrder) {
      // Small delay to ensure delete completes first
      setTimeout(() => {
        handleMyOrder(replacementOrderItem)
      }, 100)
    } else {
      // Fallback: update locally if handleMyOrder not available
      const updatedOrder = myOrderData.filter(item => item.id !== productToReplace.id)
      updatedOrder.push(replacementOrderItem)
      setMyOrderData(updatedOrder)
    }

    setAlternativesVisible(false)
    setReplaceMode(false)
    setProductToReplace(null)
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <div className='d-flex justify-content-center gap-2'>
          {isGroupMember && (
            <Button
              icon='pi pi-search'
              rounded
              outlined
              style={{
                color: '#2196F3',
                borderColor: '#2196F3'
              }}
              onClick={() => handleCheckAlternatives(rowData)}
              tooltip='Check Alternatives & Replace'
              tooltipOptions={{ position: 'top' }}
            />
          )}
          <Button
            icon='pi pi-pencil'
            rounded
            outlined
            onClick={() => {
              const match = catalogueProducts.find((p) => sameId(p.id, rowData.id))
              setRowItemData(
                match
                  ? {
                      ...match,
                      quantity: rowData.quantity,
                      tempQuantity: rowData.quantity,
                      subtotal: rowData.subtotal,
                    }
                  : rowData
              )
              setModalVisible(true)
            }}
          />
          <Button
            icon='pi pi-trash'
            rounded
            outlined
            severity='danger'
            onClick={() => {
              setDeleteOrderItem(true)
              setRowItemData(rowData)
            }}
          />
        </div>
      </React.Fragment>
    )
  }

  // Closes Delete Dialog Modal
  const hideDeleteOrderItem = () => {
    setDeleteOrderItem(false)
  }

  const handleDeleteItems = () => {
    handleDeleteItem(rowItemData)
    setDeleteOrderItem(false)
  }

  const deleteOrderItemFooter = (
    <React.Fragment>
      <Button
        label='No'
        icon='pi pi-times'
        outlined
        onClick={hideDeleteOrderItem}
      />
      <Button
        label='Yes'
        icon='pi pi-check'
        severity='danger'
        onClick={handleDeleteItems}
      />
    </React.Fragment>
  )

  const dt = useRef(null)

  const exportCSV = () => {
    dt.current.exportCSV()
  }

  const calculatePaPercentage = async () => {
    try {
      const response = await server.post(
        '/invoicesHeaders/calculate-pa-percentage',
        { products: placedSelectedOrder },
        {
          headers: {
            'x-system-name':
              typeof localStorage !== 'undefined'
                ? localStorage.getItem('module') || 'Origins Management'
                : 'Origins Management',
          },
        }
      )
      // Save the complete response data
      console.log('PA percentage calculation response:', response.data)
      setPaPercentage(response.data.paPercentage)
      setPaResponseData(response.data)
      return response.data
    } catch (error) {
      console.error('Error calculating PA percentage:', error)
      
      // Set default values to prevent order submission issues
      setPaPercentage(0)
      setPaResponseData(null)
      
      // Log the error for debugging but don't show toast to user
      // as this is a background calculation
      console.warn('PA calculation failed, proceeding with default values')
      
      return { paPercentage: 0, paAmount: 0 }
    }
  }

  const rightToolbarTemplate = () => (
    <div className='d-flex gap-2'>
      <Button
        label='Export CSV'
        icon='pi pi-upload'
        outlined
        className='p-button-help'
        onClick={exportCSV}
      />
      <Button
        type='button'
        label='Place Order'
        disabled={calculateTotal() > 0 ? false : true}
        onClick={async () => {
          console.log('Selected items:', placedSelectedOrder)
          await calculatePaPercentage() // Call the API first
          setOrderSummaryVisible(true)
        }}
      />
    </div>
  )

  return (
    <>
      <div id='componentToScrollTo' ref={orderTableRef}>
        <DataTable
          ref={dt}
          value={myOrderData ? myOrderData : []}
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          rowsPerPageOptions={rowsPerPageOptions}
          paginator
          showGridlines
          loading={tableLoading}
          dataKey='id'
          removableSort
          // globalFilterFields={['name']}
          emptyMessage={() => {
            return (
              <div
                style={{
                  height: '365px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontWeight: '400' }}>
                  The products you add to your order will appear here
                </span>
              </div>
            )
          }}
          sortField={state.sortField}
          sortOrder={state.sortOrder}
          filters={state.filters}
          rows={state.rows}
          first={state.first}
          onPage={(e) => setState((prev) => applyPageEvent(prev, e))}
          onSort={(e) => {
            e.first = 0
            setState(e)
          }}
          onFilter={(e) => setState(e)}
        >
          {/* <Column
          field='itemCode'
          header='Item    '
          //   sortable
          //   filter
          //   showFilterMenuOptions={false}
          //   filterElement={manufacturerFilterElement}
          style={{ fontWeight: 'bold' }}
        /> */}
          <Column
            field='retailUpcCode'
            header='UPC#'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={orderAmountFilterElement}
            //   body={orderAmountBodyTemplate}
          />
          <Column
            field='itemDescription'
            header='Product Description'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={paAmountFilterElement}
            //   body={paAmountBodyTemplate}
          />
          <Column
            field='manufacturer.name'
            header='Manufacturer'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={paAmountFilterElement}
            //   body={paAmountBodyTemplate}
          />
          <Column
            field='drugIdentificationNo'
            header='DIN/NPN'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={paAmountFilterElement}
            //   body={paAmountBodyTemplate}
            body={(rowData) => {
              const din = rowData.drugIdentificationNo
              if (din == null || din === '') return din
              return String(din).padStart(8, '0')
            }}
          />
          <Column
            // field='Price'
            header='Price'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={paAmountFilterElement}
            //   body={paAmountBodyTemplate}
            body={(e) => formatCurrency(e.sellingPrice)}
          />
          <Column
            field='quantity'
            header='Quantity'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={paAmountFilterElement}
            //   body={paAmountBodyTemplate}
          />
          <Column
            field='subtotal'
            header='Subtotal'
            //   sortable
            //   filter
            //   showFilterMenuOptions={false}
            //   filterElement={paAmountFilterElement}
            body={(e) => formatCurrency(e.subtotal)}
          />
          <Column body={actionBodyTemplate} style={{ minWidth: '200px' }} />
        </DataTable>
      </div>
      {myOrderData.length !== 0 && (
        <Toolbar className='mt-2' right={rightToolbarTemplate} />
      )}

      <DeleteModal
        state={deleteOrderItem}
        footer={deleteOrderItemFooter}
        onHide={hideDeleteOrderItem}
        deletedItem={true}
        name={
          rowItemData?.itemDescription ||
          rowItemData?.retailUpcCode ||
          rowItemData?.itemCode ||
          'this item'
        }
      />
      <EditOrderItem
        visible={isModalVisible}
        setVisible={setModalVisible}
        data={rowItemData}
        editItem={editItem}
        originalLineQuantity={rowItemData.quantity}
      />
      <OrderSummary
        visible={orderSummaryVisible}
        setVisible={setOrderSummaryVisible}
        orderData={myOrderData}
        total={formatCurrency(calculateTotal())}
        subtotal={formatCurrency(calculateTotal())}
        handleSuccess={(remaining = []) => {
          setKeptInDraft(remaining)
          setSuccess(true)
        }}
        ordableProducts={handleProducts}
        paPercentage={paPercentage}
        paData={paResponseData}
      />
      <SuccessModal
        visible={succcess}
        setVisible={setSuccess}
        // clear={() => clear()}
      />
      {isGroupMember && (
        <AlternativesModal
          visible={alternativesVisible}
          setVisible={setAlternativesVisible}
          product={selectedProduct}
          onReplace={replaceMode ? handleProductReplacement : null}
          replaceMode={replaceMode}
          includeOdPaAdvantage={['Origins Direct', 'Origins Direct Instant'].includes(localStorage.getItem('module') || '')}
          onAddToCart={handleAddToCartFromAlternatives}
        />
      )}
      <EditOrderItem
        visible={isAddFromAltModalVisible}
        setVisible={setAddFromAltModalVisible}
        data={productToAdd}
        originalLineQuantity={null}
        editItem={(updatedData) => {
          handleMyOrder({ ...updatedData, tempQuantity: updatedData.quantity })
        }}
      />
    </>
  )
}

export default MyOrders